import React, { useEffect, useState } from 'react';

import FavoriteIcon from '@mui/icons-material/Favorite';
import HeartBrokenIcon from '@mui/icons-material/HeartBroken';
import LinearProgress from '@mui/material/LinearProgress';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid-pro';

import { Province, ProvinceOp } from '../../../@types/KingdomResponse';
import { ProvinceLite } from '../../../@types/State';
import { useIntelState } from '../../../context/IntelState';
import { effects } from '../../../Effects';
import RestUtilities from '../../../services/RestUtilities';
import { RenderCellParams } from '../columnDefinitions/gridColumnDefinitions';
import OpsToolBar from '../components/OpsToolBar';
import { StripedDataGrid } from '../components/StripedDataGrid';
import { getSvgItemWithTooltip } from '../helpers/colHelpers';

const resizeOptions = {
  includeOutliers: true,
  includeHeaders: true,
};

const columns: GridColDef[] = [
  { field: 'id', headerName: 'ID', hideable: true },
  {
    field: 'utoDate',
    headerName: 'Date',
  },
  {
    field: 'result',
    headerName: 'Result',
    width: 40,
    renderCell: (params: RenderCellParams) =>
      params.row.result ? (
        <FavoriteIcon color='success' />
      ) : (
        <HeartBrokenIcon color='error' />
      ),
  },
  {
    field: 'provinceName',
    headerName: 'Province',
  },
  {
    field: 'targetName',
    headerName: 'Target',
  },
  {
    field: 'opIcon',
    headerName: 'Op',
    valueGetter: (params: GridValueGetterParams) => {
      const opType = params.row.opType.replaceAll('_', ' ');
      const effect =
        effects[opType.replaceAll(' ', '').replaceAll("'", '').toUpperCase()];
      return effect?.shortName;
    },
    renderCell: (params: RenderCellParams) => {
      const opType = params.row.opType.replaceAll('_', ' ');
      const effect =
        effects[opType.replaceAll(' ', '').replaceAll("'", '').toUpperCase()];
      return effect ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'start',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {getSvgItemWithTooltip(effect.svg, effect.displayName)}
          <span style={{ marginLeft: '3px' }}>{effect.shortName}</span>
        </div>
      ) : (
        opType
      );
    },
  },
  {
    field: 'duration',
    headerName: 'Duration',
    valueGetter: (params: GridValueGetterParams) =>
      params.row.duration ? `${params.row.duration} ticks` : '',
  },
  {
    field: 'gain',
    headerName: 'Gain',
    align: 'right',
    valueGetter: (params: GridValueGetterParams) =>
      params.row.gain ? `${params.row.gain}` : '',
  },
  {
    field: 'damage',
    headerName: 'Damage',
    align: 'right',
    valueGetter: (params: GridValueGetterParams) =>
      params.row.damage ? `${params.row.damage}` : '',
  },
];

const KingdomOpsGrid = () => {
  const { intelState } = useIntelState();
  const [data, setData] = useState<ProvinceOp[] | undefined>(undefined);
  const [rows, setRows] = useState<ProvinceOp[] | undefined>(undefined);
  const [opTypes, setOpTypes] = useState<string[] | undefined>(undefined);
  const [opType, setOpType] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 25,
    page: 0,
  });
  const [province, setProvince] = useState<ProvinceLite | undefined>(undefined);

  const provinces: ProvinceLite[] | undefined =
    intelState.ownData?.provinces.map((x: Province) => {
      return {
        id: x.id,
        name: x.name,
        race: x.race,
        slot: `[${x.id}]`,
      } as ProvinceLite;
    });

  const handleProvinceChange = (event: any) => {
    const prov: ProvinceLite | undefined = provinces?.find(
      (x: ProvinceLite) => x.id == event.target.value
    );
    if (prov) {
      setProvince(prov);
    } else {
      setProvince(undefined);
    }
  };

  const handleOpTypeChange = (event: any) => {
    setOpType(event.target.value);
  };

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      const response = await RestUtilities.get(
        `Kingdom/v1/KingdomOps?server=${intelState.server}`
      );
      if (response.ok) {
        const resp: ProvinceOp[] = response.content;
        setData(resp);
      } else {
        setData(undefined);
      }
      setLoading(false);
    };
    getData();
  }, [intelState.server]);

  useEffect(() => {
    let filteredData = province
      ? data?.filter((x: ProvinceOp) => x.provinceName === province.name)
      : data;
    if (opType) {
      filteredData = filteredData?.filter((o: ProvinceOp) => o.name === opType);
    }
    setRows(filteredData);
  }, [province, data, opType]);

  useEffect(() => {
    const unique = data ? Array.from(new Set(data.map((op) => op.name))) : [];
    if (unique.length) {
      setOpTypes(unique.sort());
    }
  }, [data]);

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {rows && (
        <StripedDataGrid
          autoHeight
          columns={columns}
          rows={rows}
          density='compact'
          loading={loading}
          autosizeOptions={resizeOptions}
          autosizeOnMount={true}
          paginationModel={paginationModel}
          columnVisibilityModel={{ id: false }}
          onPaginationModelChange={setPaginationModel}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          slots={{
            toolbar: OpsToolBar,
            loadingOverlay: LinearProgress,
            columnUnsortedIcon: null,
          }}
          slotProps={{
            toolbar: {
              provinces,
              province,
              handleProvinceChange,
              opTypes,
              opType,
              handleOpTypeChange,
            },
          }}
        />
      )}
    </div>
  );
};

export default KingdomOpsGrid;
